import { Component, EventEmitter, HostBinding, input, Input, OnChanges, output, Output, signal, WritableSignal } from '@angular/core';
import { GetKeyPipe } from 'app/shared/pipes/getkey.pipe';
import { MediaService } from 'app/services/media/media.service';
import { AppService } from 'app/app.service';
import { MediaInfoResponse } from '@key-telematics/fleet-api-client';
import { TranslateService } from '@ngx-translate/core';
import { FilterPanelService } from 'app/shared/components/filter-panel/filter-panel.service';
import moment from 'moment-timezone';

@Component({
    selector: 'key-video-list-item',
    templateUrl: 'list-item.component.html',
    styleUrls: ['./list-item.component.scss'],
    standalone: false
})
export class VideoListItemComponent implements OnChanges {
    
    title: string;
    description: string;
    thumbnailUrls: string[];
    values: { title: string, value: string }[] = [];
    eventSpecificDescription: string;

    @Input() data: MediaInfoResponse;
    @Input() showFilterByValue = true;

    //Because this component is used in two different areas, one requires column display and the other requires row display. This allows the component to determine if the column class needs to be applied.
    @Input() isColumnLayout = true;

    @Output() selected: EventEmitter<MediaInfoResponse> = new EventEmitter();
    
    @HostBinding('class.muted') muted = false;

    layout = 'default';
    classNames: string[] = [];
    showSaveVideoButton = input<boolean>(false);
    showDeleteVideoButton = input<boolean>(false);
    saveVideo = output<MediaInfoResponse>();
    deleteVideo = output<MediaInfoResponse>();
    saveVideoBtnDisabledTooltip: WritableSignal<string> = signal('');
    savedFrom: string;

    constructor(
        private app: AppService,
        public getKey: GetKeyPipe,
        private i18n: TranslateService,
        private filterService: FilterPanelService,
        public media: MediaService
    ) {
        if (this.app?.features?.page?.overview?.layout === 'basic') {
            this.layout = 'basic';
            this.classNames.push('layout-right');
        }
    }

    ngOnChanges() {
        if (this.data) {
            this.setToolTipMessage();

            this.thumbnailUrls = [this.media.getThumbnailsUrl(this.data.asset.id, {
                date: this.data.date,
                filename: this.data.filename,
                input: this.data.input,
                mediaType: 'video',
            })];

            this.title = this.data.asset.name;
            this.description = this.data.address;

            // if a video has an 'ai' label, and a 'src/event' label i.e. not a 'src/user' label
            // it is 'event triggered video that is has been triggered by an AI event'
            const isAiEventTriggered = !!this.data.labels.find(x => x.label === 'ai') && !!this.data.labels.find(x => x.label === 'src/event');
            if (isAiEventTriggered) {
                this.eventSpecificDescription = 
                `${this.i18n.instant('VIDEOS.LIST.AI_EVENT')} - ${this.i18n.instant('SHARED.EVENTS.AIEVENT.TYPES.' + this.data.eventType.toUpperCase() + '.SHORT_DESC')}`;

                this.savedFrom = this.i18n.instant('VIDEOS.MESSAGES.SAVED_FROM_AI_TRIGGERED');
            }

            if (!isAiEventTriggered && this.data.requester.type !== 'user') {
                // is event triggered, that is not ai, and not requested by a user
                this.eventSpecificDescription =
                `${this.data.requester.name} ${this.i18n.instant('VIDEOS.DETAILS.EVENT')}`

                this.savedFrom = this.i18n.instant('VIDEOS.MESSAGES.SAVED_FROM_EVENT_TRIGGERED');
            }

            if (!isAiEventTriggered && this.data.requester.type === 'user') {
                this.eventSpecificDescription = this.i18n.instant('VIDEOS.LIST.REQUESTED_BY', { requester: this.data.requester.name });
                this.savedFrom = this.i18n.instant('VIDEOS.MESSAGES.SAVED_FROM_REQUESTED');
            }

            const customFields = this.app.client?.customFields?.event || [];
            const rawValues = {};

            this.values = Object.keys(this.data.fields || {}).map(key => ({
                title: customFields.find(x => x.id === key)?.title || key,
                value: rawValues[key],
            }));
        }
    }

    getDisplayDate(date: string): string {
        return date ? moment.utc(date).fromNow() : '';
    }

    filterBy(event: Event, type: string) {
        event.stopPropagation();
        switch (type) {
            case 'asset': {
                this.filterService.addFilterValues('assets', [this.data.asset]);
                break;
            }
            case 'requester': {
                this.filterService.addFilterValues('requester', [this.data.requester]);
                break;
            }
            case 'status': {
                this.filterService.addFilterValues('status', [{ id: this.data.status, name: this.i18n.instant('MEDIA.STATUS.TITLE.' + this.data.status?.toUpperCase()) }]);
                break;
            }
            case 'eventClass': {
                this.filterService.addFilterValues('eventClass', [{ id: this.data.eventClass, name: this.i18n.instant('SHARED.EVENTS.' + this.data.eventClass.toUpperCase() + '.NAME') }]);
                break;
            }
            case 'input': {
                this.filterService.addFilterValues('input', [{ id: this.data.input, name: this.data.input }]);
                break;
            }

        }
    }

    setToolTipMessage() {
        if (this.data.status === 'ready') {
            this.saveVideoBtnDisabledTooltip.set('');
        } else {
            this.saveVideoBtnDisabledTooltip.set(this.i18n.instant('VIDEOS.MESSAGES.CANNOT_SAVE_VIDEOS_NOT_READY'));
        }
    }
}

